import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export function CustomDropdown({ value, onChange, options, placeholder, label, required, openUpward = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
        {label}
        {required && <span className="ml-1" style={{ color: '#ef4444' }}>*</span>}
      </label>

      {/* Display Field */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 rounded-xl font-medium focus:outline-none transition-all duration-200 hover:shadow-md shadow-sm text-left flex items-center justify-between"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          color: 'var(--text-primary)',
        }}
      >
        <span style={{ color: !selectedOption ? 'var(--text-muted)' : 'var(--text-primary)' }}>
          {displayText}
        </span>
        <ChevronDown
          className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--text-secondary)' }}
        />
      </button>

      {/* Dropdown List */}
      {isOpen && (
        <div
          className={`absolute z-50 rounded-xl shadow-2xl w-full max-h-60 overflow-y-auto ${openUpward ? 'bottom-full mb-2' : 'mt-2'}`}
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          }}
        >
          {options.map((option, index) => {
            const isSelected = value === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`
                  w-full px-4 py-2.5 text-left text-sm font-medium transition-all duration-200 flex items-center justify-between
                  ${index === 0 ? 'rounded-t-xl' : ''}
                  ${index === options.length - 1 ? 'rounded-b-xl' : ''}
                `}
                style={{
                  background: isSelected
                    ? 'linear-gradient(135deg, var(--primary-hover), var(--primary))'
                    : 'transparent',
                  color: isSelected ? 'var(--bg-base)' : 'var(--text-primary)',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'var(--highlight-10)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <span>{option.label}</span>
                {isSelected && <Check className="w-4 h-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
